import { useEffect, useMemo, useRef, useState } from "react";
import SelecaoMultipla from "../Catalog/SelecaoMultipla";
import YearMultiSelect from "../Catalog/YearMultiSelect";
import { FiCalendar, FiFilter } from "react-icons/fi";
import useSWR from "swr";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  SimpleGrid,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import baseService from "../../services/base";
import Loader from "../../components/Feedback/Loader";
import Narrative from "./Narrative";
import KpiCard from "./KpiCard";
import { COLORS } from "../../theme/dashboardTokens";

// EG-38 — Histórico: por ANO (evolução) OU por CERTIFICADO (comparar selos lado
// a lado). Reusa GET /metrics/history e /metrics/history/certificates.

const fetcher = (url) => baseService.get(url).then((r) => r?.data);

// Cores por exercício (série do gráfico "por certificado").
const YEAR_COLORS = [COLORS.brand, COLORS.maturity, "#7C5CFC", COLORS.warn, "#0EA5A5"];

function ChartTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <Box bg={COLORS.surface} border="1px solid #E2E8F0" borderRadius="8px" p="10px" boxShadow="sm">
      <Text fontSize="13px" fontWeight={600} color={COLORS.ink}>
        {label}
      </Text>
      <Text fontSize="12px" color={COLORS.inkMuted}>
        Nota média {row.avgScore} · {row.companies} empresas{unit}
      </Text>
    </Box>
  );
}

function ModeButton({ active, children, onClick }) {
  return (
    <Button
      size="sm"
      variant={active ? "solid" : "outline"}
      bg={active ? COLORS.brand : "white"}
      color={active ? "white" : COLORS.inkMuted}
      borderColor="#E2E8F0"
      _hover={{ bg: active ? COLORS.brand : "#F0F0F0" }}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

// Quantos certificados vêm marcados na primeira visita. Poucos o bastante
// para o gráfico nascer legível, recentes o bastante para serem os que
// quase sempre interessam.
const QUANTIDADE_INICIAL = 8;

export default function HistoryDashboard() {
  const [mode, setMode] = useState("year"); // 'year' | 'certificate'
  // Antes era EXCLUSÃO (tudo ligado, clique desliga). Com poucos certificados
  // funcionava; com cem o gráfico já nasce ilegível e o usuário precisa
  // desligar 90 antes de enxergar algo. Agora é SELEÇÃO — ele escolhe o que
  // quer comparar.
  const [selecionados, setSelecionados] = useState([]);
  const [anosSelecionados, setAnosSelecionados] = useState([]);
  // Um drawer POR FILTRO: juntos, o de certificados (com busca e lista longa)
  // empurrava o de anos para baixo da dobra, e quem queria só trocar o ano
  // precisava rolar por toda a lista de certificados.
  const {
    isOpen: certificadosAberto,
    onOpen: onOpenCertificados,
    onClose: onCloseCertificados,
  } = useDisclosure();
  const {
    isOpen: anosAberto,
    onOpen: onOpenAnos,
    onClose: onCloseAnos,
  } = useDisclosure();
  // Um default vazio mostraria gráfico vazio na primeira visita; um default
  // "tudo" recria o problema. Os mais recentes são o que quase sempre se quer
  // ver, e o usuário ajusta a partir daí.
  const preencheuInicial = useRef(false);

  const { data: yearData, isLoading } = useSWR("metrics/history", fetcher);
  const { data: certData } = useSWR("metrics/history/certificates", fetcher);

  const points = yearData?.points ?? [];
  const allCerts = useMemo(() => certData?.certificates ?? [], [certData]);


  useEffect(() => {
    if (preencheuInicial.current || allCerts.length === 0) return;
    preencheuInicial.current = true;

    const maisRecentes = [...allCerts]
      .sort((a, b) => (b.exercise ?? 0) - (a.exercise ?? 0))
      .slice(0, QUANTIDADE_INICIAL)
      .map((c) => String(c.id));

    setSelecionados(maisRecentes);
  }, [allCerts]);

  const anosDisponiveis = useMemo(
    () => [...new Set(allCerts.map((c) => c.exercise).filter((e) => e != null))].sort(),
    [allCerts]
  );

  const included = useMemo(() => {
    const escolhidos = new Set(selecionados);
    return allCerts.filter((c) => {
      if (escolhidos.size > 0 && !escolhidos.has(String(c.id))) return false;
      // Filtro de ano vazio = todos os anos; senão, só os escolhidos.
      if (anosSelecionados.length > 0 && !anosSelecionados.includes(c.exercise)) {
        return false;
      }
      return true;
    });
  }, [allCerts, selecionados, anosSelecionados]);

  const opcoesDeCertificado = useMemo(
    () =>
      allCerts.map((c) => ({
        value: String(c.id),
        label: c.exercise ? `${c.name} (${c.exercise})` : c.name,
      })),
    [allCerts]
  );

  // Comparar o MESMO certificado entre anos: pivota por NOME, uma série por
  // exercício. Ex.: "testePilaresCert" com barras 2025 e 2026 lado a lado.
  const years = useMemo(
    () => [...new Set(included.map((c) => c.exercise).filter((e) => e != null))].sort(),
    [included]
  );
  const pivoted = useMemo(() => {
    const byName = new Map();
    included.forEach((c) => {
      const row = byName.get(c.name) || { name: c.name };
      const key = c.exercise != null ? String(c.exercise) : "sem ano";
      row[key] = c.avgScore;
      byName.set(c.name, row);
    });
    return [...byName.values()];
  }, [included]);

  // Chaves de série do gráfico: os anos presentes (+ "sem ano" se houver).
  const seriesKeys = useMemo(() => {
    const keys = years.map(String);
    if (pivoted.some((r) => r["sem ano"] != null)) keys.push("sem ano");
    return keys;
  }, [years, pivoted]);

  if (isLoading) return <Loader />;

  const latest = points[points.length - 1];
  const previous = points.length >= 2 ? points[points.length - 2] : null;
  const delta = previous ? Math.round((latest.avgScore - previous.avgScore) * 100) / 100 : null;

  // Narrativa por modo.
  let narrative = "";
  let tone = "info";
  if (mode === "year") {
    narrative = previous
      ? `A nota média do portfólio ${delta >= 0 ? "subiu" : "caiu"} de ${previous.avgScore} (${
          previous.year
        }) para ${latest.avgScore} (${latest.year}) — ${delta >= 0 ? "+" : ""}${delta}.`
      : latest
      ? `Primeiro exercício com dados (${latest.year}): nota média ${latest.avgScore}.`
      : "";
    tone = delta != null && delta < 0 ? "warn" : "info";
  } else if (included.length >= 1) {
    // Se algum certificado tem >1 ano, narra a evolução DELE (o pedido).
    const multi = pivoted.find(
      (r) => years.filter((y) => r[String(y)] != null).length >= 2
    );
    if (multi) {
      const ys = years.filter((y) => multi[String(y)] != null);
      const first = multi[String(ys[0])];
      const last = multi[String(ys[ys.length - 1])];
      const d = Math.round((last - first) * 100) / 100;
      narrative = `${multi.name}: ${d >= 0 ? "subiu" : "caiu"} de ${first} (${ys[0]}) para ${last} (${
        ys[ys.length - 1]
      }) — ${d >= 0 ? "+" : ""}${d}.`;
      tone = d < 0 ? "warn" : "good";
    } else {
      const sorted = [...included].sort((a, b) => b.avgScore - a.avgScore);
      narrative = `Maior nota média: ${sorted[0].name} (${sorted[0].avgScore}); menor: ${
        sorted[sorted.length - 1].name
      } (${sorted[sorted.length - 1].avgScore}).`;
    }
  }

  const emptyYear = mode === "year" && points.length === 0;

  return (
    <Box>
      {/* Filtro de modo */}
      <Flex gap="8px" mb="16px" align="center" flexWrap="wrap">
        <Text fontSize="13px" color={COLORS.inkMuted} mr="4px">
          Comparar por
        </Text>
        <ModeButton active={mode === "year"} onClick={() => setMode("year")}>
          Ano
        </ModeButton>
        <ModeButton active={mode === "certificate"} onClick={() => setMode("certificate")}>
          Certificado
        </ModeButton>
      </Flex>

      {narrative && <Narrative tone={tone}>{narrative}</Narrative>}

      {mode === "year" ? (
        emptyYear ? (
          <Flex bg={COLORS.surface} borderRadius="12px" p="48px" justify="center">
            <Text color={COLORS.inkMuted}>
              A comparação por ano aparece com resultados em mais de um exercício.
            </Text>
          </Flex>
        ) : (
          <>
            <SimpleGrid columns={{ base: 1, sm: 3 }} spacing="16px" mb="24px">
              <KpiCard label={`Nota média ${latest.year}`} value={latest.avgScore} color={COLORS.brand} />
              {delta !== null && (
                <KpiCard
                  label={`${previous?.year} → ${latest.year}`}
                  value={`${delta >= 0 ? "+" : ""}${delta}`}
                  color={delta >= 0 ? COLORS.good : COLORS.warn}
                />
              )}
            </SimpleGrid>
            <Box bg={COLORS.surface} borderRadius="12px" p={["20px", "28px"]}>
              <Heading size="sm" color={COLORS.ink} mb="16px">
                Nota média do portfólio por ano
              </Heading>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={points} margin={{ top: 20, right: 8, bottom: 8, left: -12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EDF2F7" vertical={false} />
                  <XAxis dataKey="year" tick={{ fontSize: 12, fill: COLORS.inkMuted }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
                  <YAxis tick={{ fontSize: 12, fill: COLORS.inkMuted }} tickLine={false} axisLine={false} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(0,115,187,0.06)" }} />
                  <Bar dataKey="avgScore" fill={COLORS.brand} radius={[4, 4, 0, 0]} maxBarSize={72}>
                    <LabelList dataKey="avgScore" position="top" fontSize={12} fill={COLORS.inkMuted} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </>
        )
      ) : (
        <>
          {/* Os filtros moram num drawer: as pílulas dos certificados
              escolhidos empurravam o gráfico para baixo, e com muitos
              certificados o filtro ocupava mais tela que o dado. Aqui fica só
              o botão com a contagem. */}
          {allCerts.length > 0 && (
            <Flex gap="12px" wrap="wrap" mb="16px" align="center">
              <Button
                onClick={onOpenCertificados}
                variant="outline"
                h="40px"
                leftIcon={<FiFilter />}
                fontWeight={500}
                fontSize="sm"
              >
                {selecionados.length > 0
                  ? `${selecionados.length} certificado${selecionados.length > 1 ? "s" : ""}`
                  : "Certificados"}
              </Button>

              {anosDisponiveis.length > 1 && (
                <Button
                  onClick={onOpenAnos}
                  variant="outline"
                  h="40px"
                  leftIcon={<FiCalendar />}
                  fontWeight={500}
                  fontSize="sm"
                >
                  {anosSelecionados.length > 0
                    ? `${anosSelecionados.length} ano${anosSelecionados.length > 1 ? "s" : ""}`
                    : "Todos os anos"}
                </Button>
              )}
            </Flex>
          )}

          <Drawer
            isOpen={certificadosAberto}
            onClose={onCloseCertificados}
            placement="right"
            size="md"
          >
            <DrawerOverlay />
            <DrawerContent>
              <DrawerCloseButton />
              <DrawerHeader fontSize="md">Certificados</DrawerHeader>

              <DrawerBody>
                <Text fontSize="12px" color={COLORS.inkMuted} mb="10px">
                  Escolha quais certificados comparar. Sem nenhum marcado, o
                  gráfico mostra todos.
                </Text>
                <SelecaoMultipla
                  options={opcoesDeCertificado}
                  value={selecionados}
                  onChange={setSelecionados}
                  placeholder="Todos os certificados"
                  searchPlaceholder="Buscar certificado"
                  countLabel={(n) => `${n} certificado${n > 1 ? "s" : ""}`}
                />
              </DrawerBody>

              <DrawerFooter borderTop="1px solid #EDF2F7" justifyContent="space-between">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelecionados(opcoesDeCertificado.map((o) => o.value))}
                  isDisabled={selecionados.length === opcoesDeCertificado.length}
                >
                  Selecionar todos
                </Button>
                <Flex gap="8px">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelecionados([])}
                    isDisabled={selecionados.length === 0}
                  >
                    Limpar
                  </Button>
                  <Button size="sm" colorScheme="blue" onClick={onCloseCertificados}>
                    Concluir
                  </Button>
                </Flex>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>

          <Drawer isOpen={anosAberto} onClose={onCloseAnos} placement="right" size="xs">
            <DrawerOverlay />
            <DrawerContent>
              <DrawerCloseButton />
              <DrawerHeader fontSize="md">Anos</DrawerHeader>

              <DrawerBody>
                <Text fontSize="12px" color={COLORS.inkMuted} mb="10px">
                  Sem nenhum ano marcado, o gráfico mostra todos.
                </Text>
                <YearMultiSelect
                  years={anosDisponiveis}
                  value={anosSelecionados}
                  onChange={setAnosSelecionados}
                />
              </DrawerBody>

              <DrawerFooter borderTop="1px solid #EDF2F7" justifyContent="space-between">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAnosSelecionados([...anosDisponiveis])}
                  isDisabled={anosSelecionados.length === anosDisponiveis.length}
                >
                  Selecionar todos
                </Button>
                <Flex gap="8px">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setAnosSelecionados([])}
                    isDisabled={anosSelecionados.length === 0}
                  >
                    Limpar
                  </Button>
                  <Button size="sm" colorScheme="blue" onClick={onCloseAnos}>
                    Concluir
                  </Button>
                </Flex>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>

          <Box bg={COLORS.surface} borderRadius="12px" p={["20px", "28px"]}>
            <Heading size="sm" color={COLORS.ink} mb="4px">
              Nota média por certificado — comparação entre anos
            </Heading>
            <Text fontSize="12px" color={COLORS.inkMuted} mb="16px">
              Cada certificado com uma barra por exercício: o mesmo certificado (ex.: testePilaresCert)
              aparece com 2025 e 2026 lado a lado. Use os filtros acima para escolher o que comparar.
            </Text>
            {pivoted.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(280, pivoted.length * 64)}>
                <BarChart data={pivoted} margin={{ top: 20, right: 8, bottom: 8, left: -12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EDF2F7" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: COLORS.inkMuted }}
                    tickLine={false}
                    axisLine={{ stroke: "#E2E8F0" }}
                    interval={0}
                    angle={pivoted.length > 3 ? -15 : 0}
                    textAnchor={pivoted.length > 3 ? "end" : "middle"}
                    height={pivoted.length > 3 ? 60 : 30}
                  />
                  <YAxis tick={{ fontSize: 12, fill: COLORS.inkMuted }} tickLine={false} axisLine={false} />
                  <Tooltip cursor={{ fill: "rgba(0,115,187,0.06)" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {seriesKeys.map((k, i) => (
                    <Bar key={k} dataKey={k} name={k} fill={YEAR_COLORS[i % YEAR_COLORS.length]} radius={[4, 4, 0, 0]} maxBarSize={44}>
                      <LabelList dataKey={k} position="top" fontSize={11} fill={COLORS.inkMuted} />
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Text color={COLORS.inkMuted} fontSize="14px">
                Selecione ao menos um certificado.
              </Text>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
